<template>
  <div id="sidebar-menu">
    <ul class="sidebar-links custom-scrollbar" id="myDIV">
      <li
          v-for="(menuItem, index) in filteredMenuItems"
          :key="index"
          class="sidebar-list"
          :class="{
            'active-parent': isActiveMenu(menuItem) && !menuItem.children,
            'has-active-child': isActiveMenu(menuItem) && menuItem.children
          }"
      >
        <!-- Render head title -->
        <div v-if="menuItem.type === 'headtitle'" class="sidebar-headtitle">
          <h6>{{ menuItem.headTitle1 }}</h6>
          <h6 v-if="menuItem.headTitle2">{{ menuItem.headTitle2 }}</h6>
        </div>

        <!-- Render submenu parent -->
        <a
            href="javascript:void(0)"
            class="sidebar-link sidebar-title"
            v-if="menuItem.type === 'sub'"
            @click="menuItem.children ? setNavActive(menuItem) : navigateTo(menuItem)"
            :class="{
              'active-link': isActiveMenu(menuItem) && !menuItem.children,
              'active-parent-link': isActiveMenu(menuItem) && menuItem.children
            }"
        >
          <i :class="[menuItem.faIcon, 'me-2']"></i>
          <span>{{ menuItem.title }}</span>
          <div class="according-menu" v-if="menuItem.children">
            <i :class="['fa', menuItem.active ? 'fa-angle-down' : 'fa-angle-right']"></i>
          </div>
        </a>

        <!-- Render link items -->
        <router-link
            v-else-if="menuItem.type === 'link'"
            :to="menuItem.path"
            class="sidebar-link sidebar-title"
            @click="navigateTo(menuItem)"
            :class="{
              'active-link': isActiveMenu(menuItem)
            }"
        >
          <svg class="stroke-icon">
            <use :xlink:href="require('@/assets/svg/icon-sprite.svg') + `#${menuItem.icon}`"></use>
          </svg>
          <svg class="fill-icon">
            <use :xlink:href="require('@/assets/svg/icon-sprite.svg') + `#${menuItem.iconf}`"></use>
          </svg>
          <span>{{ menuItem.title }}</span>
        </router-link>

        <!-- Render submenu children with enhanced tree view -->
        <ul
            class="sidebar-submenu"
            v-if="menuItem.children"
            :class="{ 'menu-open': menuItem.active }"
        >
          <li
              v-for="(child, childIndex) in menuItem.children"
              :key="childIndex"
              class="submenu-item"
              :class="{
                'first-child': childIndex === 0,
                'last-child': childIndex === menuItem.children.length - 1,
                'active-child': isActiveMenu(child)
              }"
          >
            <router-link
                :to="child.path"
                class="sidebar-link submenu-link"
                @click="navigateTo(child, true)"
            >
              <!-- Tree view connector lines -->
              <div class="tree-connector">
                <div class="tree-line-vertical"></div>
                <div class="tree-line-horizontal"></div>
              </div>

              <span>{{ child.title }}</span>
            </router-link>
          </li>
        </ul>
      </li>
    </ul>
  </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
  data() {
    return {
      activeRoute: this.$route.path,
    };
  },

  computed: {
    ...mapState('menu', ['filteredMenuItems'])
  },

  watch: {
    $route(to) {
      this.activeRoute = to.path;
    }
  },

  created() {
    this.$store.dispatch('menu/initializeMenu');
  },

  methods: {

    setNavActive(menuItem) {
      menuItem.active = !menuItem.active;
    },

    navigateTo(menuItem, isChild = false) {
      if (menuItem.path) {
        this.$router.push(menuItem.path);

        // Optional: close submenu after selecting a child
        if (isChild) {
          this.filteredMenuItems.forEach(item => {
            if (item.children) item.active = false;
          });
        }
      }
    },

    isActiveMenu(menuItem) {
      if (menuItem.path && this.activeRoute === menuItem.path) {
        return true;
      }

      if (menuItem.children) {
        return menuItem.children.some(child => child.path === this.activeRoute);
      }

      return false;
    },

    logout() {
      localStorage.removeItem('token_set');
      localStorage.removeItem('profile_id');
      localStorage.removeItem('allowedRoutes');
      this.$store.commit('menu/CLEAR_MENU_STATE');
      this.redirectToLogin();
    },

    redirectToLogin() {
      this.$router.replace({ path: '/login' });
    },
  }
};
</script>

<style scoped>
.sidebar-links {
  list-style: none;
  padding: 0;
  margin: 0;
}

.sidebar-list {
  margin-bottom: 5px;
  position: relative;
}

.sidebar-headtitle {
  padding: 15px 20px 10px;
  margin-top: 15px;
}

.sidebar-headtitle h6 {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #95a5b8;
  margin: 0;
}

.sidebar-link {
  display: flex;
  align-items: center;
  padding: 12px 20px;
  text-decoration: none;
  color: #6c757d;
  position: relative;
  transition: all 0.3s ease;
  border-left: 3px solid transparent;
}

/* Default states */
.sidebar-link span,
.sidebar-link i,
.stroke-icon,
.fill-icon {
  color: #6c757d;
  transition: all 0.3s ease;
}

.sidebar-link:hover {
  background-color: #f8f9fa;
}

.sidebar-link:hover span,
.sidebar-link:hover i {
  color: #143D60 !important;
  font-weight: bold;
}

/* ACTIVE STATE FOR PARENT ITEMS WITHOUT CHILDREN */
.active-link {
  background-color: #e9ecef !important;
  border-left: 3px solid #143D60 !important;
}

.active-link span,
.active-link i,
.active-link .stroke-icon,
.active-link .fill-icon {
  color: #143D60 !important;
  font-weight: bold !important;
}

/* Active state for parent items that have active children */
.active-parent-link {
  background-color: #f0f4f8 !important;
  border-left: 3px solid #143D60 !important;
}

.active-parent-link span,
.active-parent-link i,
.active-parent-link .stroke-icon,
.active-parent-link .fill-icon {
  color: #143D60 !important;
  font-weight: bold !important;
}

.sidebar-title {
  cursor: pointer;
}

.according-menu {
  margin-left: auto;
}

.according-menu i {
  transition: transform 0.3s ease;
  font-size: 12px;
}

/* Submenu Styles */
.sidebar-submenu {
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;
  background-color: #f8f9fa;
  position: relative;
}

.menu-open {
  max-height: 1000px;
}

.submenu-item {
  position: relative;
  transition: all 0.3s ease;
}

.submenu-link {
  padding: 10px 20px 10px 60px !important;
  font-size: 14px;
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  border-left: 3px solid transparent;
}

.submenu-link span {
  margin-left: 8px;
}

/* Enhanced Tree View Connector */
.tree-connector {
  position: absolute;
  left: 30px;
  top: 0;
  bottom: 0;
  width: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tree-line-vertical {
  position: absolute;
  left: 9px;
  top: 0;
  bottom: 0;
  width: 2px;
  background-color: #d1d5db;
  transition: background-color 0.3s ease;
}

.tree-line-horizontal {
  position: absolute;
  left: 9px;
  top: 50%;
  width: 12px;
  height: 2px;
  background-color: #d1d5db;
  transform: translateY(-50%);
  transition: background-color 0.3s ease;
}

/* First child adjustments */
.submenu-item.first-child .tree-line-vertical {
  top: 0;
  height: 100%;
}

.submenu-item.last-child .tree-line-vertical {
  bottom: 50%;
  height: 50%;
}

.submenu-item:only-child .tree-line-vertical {
  top: 0;
  bottom: 50%;
  height: 50%;
}

.sidebar-list:has(.sidebar-submenu) .submenu-item.first-child .tree-line-vertical {
  top: -20px;
  height: calc(100% + 20px);
}

/* Active child styling */
.active-child .submenu-link {
  background-color: #e9ecef;
  border-left: 3px solid #143D60;
}

.active-child .submenu-link span {
  color: #143D60 !important;
  font-weight: bold;
}

.active-child .tree-line-horizontal,
.active-child .tree-line-vertical {
  background-color: #143D60;
}

/* Hover effects for tree view */
.submenu-link:hover .tree-line-horizontal,
.submenu-link:hover .tree-line-vertical {
  background-color: #143D60;
}

.submenu-link:hover {
  background-color: #f8f9fa;
}

.submenu-link:hover span {
  color: #143D60 !important;
  font-weight: bold;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .submenu-link {
    padding: 8px 15px 8px 55px !important;
  }

  .tree-connector {
    left: 25px;
  }
}

/* Custom Scrollbar */
.custom-scrollbar {
  max-height: calc(100vh - 120px);
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
  scrollbar-color: #d1d5db #f1f1f1;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 10px;
  border: 1px solid #f1f1f1;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

/* Smooth transitions */
.sidebar-submenu,
.tree-connector *,
.submenu-link,
.sidebar-link {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
</style>